import { CTA, Spinner, styled } from '@volkswagen-onehub/components-core';
import { ArrowRight } from '@volkswagen-onehub/icons-core/dist/ArrowRight';
import { Checkmark } from '@volkswagen-onehub/icons-core/dist/Checkmark';
import { Phone } from '@volkswagen-onehub/icons-core/dist/Phone';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrefijoInput, TelefonoInput } from 'src/components';
import {
	getTrackingForm,
	useOneFormContext,
	useTrackingManager,
} from 'src/feature-app';
import { OneFormState } from 'src/types';

const COLOR = {
	GREEN: '#029640',
	YELLOW: '#ffaa27',
	YELLOW_HOVER: '#cf9a17',
	GREY: '#DFE4E8',
};

const RaceFormWrapper = styled.div`
	display: flex;
	gap: 20px;
	margin-bottom: 24px;
	margin-top: 24px;
	width: 100%;
	flex-direction: row;
	@media all and (max-width: 560px) {
		flex-direction: column;
	}
`;

const PrefijoWrapper = styled.div`
	width: 100px;
	@media all and (max-width: 560px) {
		width: 100%;
	}
`;

const TelefonoWrapper = styled.div`
	width: 160px;
	@media all and (max-width: 560px) {
		width: 100%;
	}
`;

interface CTAWrapperProps {
	isSending: boolean;
	isSent: boolean;
	isPristine: boolean;
	showFullWidth: boolean;
	successful: boolean;
}
const CTAWrapper = styled.div<CTAWrapperProps>`
	& button {
		width: 100%;
		height: 44px;
		cursor: ${props => (props.isSent ? 'auto' : 'pointer')};
		background-color: ${props =>
			props.successful ? COLOR.GREEN : COLOR.YELLOW};
		border: ${props =>
			props.successful
				? `2px solid ${COLOR.GREEN}`
				: `2px solid ${COLOR.YELLOW}`};

		& > span {
			height: 24px;
		}
	}
	& button:hover {
		background-color: ${props =>
			props.successful
				? `${COLOR.GREEN} !important`
				: props.isSending
				? COLOR.GREY
				: `${COLOR.YELLOW_HOVER} !important`};
		border: ${props =>
			props.successful
				? `2px solid ${COLOR.GREEN}`
				: `2px solid ${COLOR.YELLOW_HOVER}`};
	}
	& button[disabled] {
		cursor: not-allowed;
		color: ${props => (props.successful ? '#ffffff' : 'inherit')};
		background-color: ${props =>
			props.successful ? COLOR.GREEN : COLOR.GREY};
		border: 2px ${props => (props.successful ? COLOR.GREEN : COLOR.GREY)};
	}
	& button[disabled]:hover {
		background-color: ${COLOR.GREY} !important;
	}
`;

interface RaceFormProps {
	isSent: boolean;
	isSending: boolean;
	disabledButton: boolean;
	sendForm: () => Promise<boolean>;
	isPristine: boolean;
	isFullScreen: boolean;
	copyWidth?: number; // Con el resize en algunos dispositivos móviles se pierde el tamaño necesario del cta y se descuadra.
	setIsPristine: React.Dispatch<React.SetStateAction<boolean>>;
	successful?: boolean;
	error?: boolean;
}

const showFullCta = (isFullScreen: boolean) =>
	isFullScreen && window.innerWidth > 1279;

export const RaceForm = ({
	isSent,
	isSending,
	isPristine,
	disabledButton,
	sendForm,
	copyWidth,
	isFullScreen,
	setIsPristine,
	successful,
}: RaceFormProps) => {
	const { initializeForm } = useOneFormContext();
	const { formInfo } = useSelector((state: OneFormState) => state);
	const { formResponse } = formInfo;
	const trackingManager = useTrackingManager();
	const dispatch = useDispatch();

	useEffect(() => {
		initializeForm(
			{},
			{
				headers: {},
				fields: {
					formName: 'race',
					formType: 'otherform',
					pageCategory: 'other',
				},
			},
			isFullScreen,
		);
	}, []);

	useEffect(() => {
		if (successful) {
			dispatch({ type: 'SET_FORM_SUBMITTED', payload: true });

			trackingManager.trackFormSubmissionSuccessMessageLoad(
				{
					contentId: undefined,
				},
				getTrackingForm(formResponse),
			);
		}
	}, [successful]);

	return (
		<RaceFormWrapper className="race-form-wrapper">
			{isSending || isSent ? null : (
				<>
					<PrefijoWrapper>
						<PrefijoInput name="racePrefix" />
					</PrefijoWrapper>
					<TelefonoWrapper>
						<TelefonoInput
							required
							onChangeHandler={() => {
								setIsPristine(false);
							}}
							onFocusHandler={() => {
								setIsPristine(false);
							}}
							name="raceCellphone"
						/>
					</TelefonoWrapper>
				</>
			)}

			<CTAWrapper
				isSending={isSending}
				isSent={isSent}
				isPristine={isPristine}
				successful={successful}
				showFullWidth={isFullScreen}
			>
				<CTA
					tag="button"
					disabled={disabledButton || isSending || successful}
					icon={
						isSending ? (
							<Spinner />
						) : successful ? (
							<Checkmark />
						) : showFullCta(isFullScreen) ? (
							<Phone />
						) : (
							<ArrowRight />
						)
					}
					onClick={async () => {
						if (!isSending && !isSent) {
							await sendForm();
						}
					}}
					stretchContent
				>
					{isSending
						? 'Enviando'
						: successful
						? 'Te llamaremos pronto'
						: showFullCta(isFullScreen)
						? 'Te llamamos'
						: null}
				</CTA>
			</CTAWrapper>
		</RaceFormWrapper>
	);
};
